import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { ResetPasswordPage } from './auth/ResetPasswordPage';
import { AppShell } from './components/AppShell';
import { BoardPage } from './board/BoardPage';
import { FieldDashboard } from './field/FieldDashboard';
import { ReviewQueuePage } from './review/ReviewQueuePage';
import { ActivityLogPage } from './activity/ActivityLogPage';
import { DispatchBoardPage } from './dispatch/DispatchBoardPage';
import { SuperPortalPage } from './super/SuperPortalPage';
import { InvoiceQueuePage } from './invoicing/InvoiceQueuePage';
import { ReportsPage } from './reporting/ReportsPage';
import { WeatherPage } from './weather/WeatherPage';
import { AdminPage } from './admin/AdminPage';
import { ServiceBoardPage } from './service/ServiceBoardPage';

export function App() {
    const { session, loading, role, passwordRecovery } = useAuth();

    // Password recovery: user clicked the reset email link. Show the
    // set-new-password page regardless of session state. Once they submit
    // a new password, AuthProvider clears the flag and normal flow resumes.
    if (passwordRecovery) {
        return <ResetPasswordPage />;
    }

    // Initial session load
    if (loading && !session) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-graphite-50 dark:bg-graphite-950">
                <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                    Loading...
                </div>
            </div>
        );
    }

    // Not logged in
    if (!session) {
        return <LoginPage />;
    }

    // Logged in but profile row missing (new user without admin provision)
    if (!role) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-graphite-50 dark:bg-graphite-950 px-6">
                <div className="max-w-sm text-center">
                    <p className="font-display font-semibold text-lg text-graphite-900 dark:text-graphite-100 mb-2">
                        Account not provisioned
                    </p>
                    <p className="text-sm text-graphite-600 dark:text-graphite-400">
                        Your login succeeded but no role has been assigned yet. Contact Andrea
                        to finish account setup.
                    </p>
                </div>
            </div>
        );
    }

    // Superintendents get the standalone submission portal. They do not
    // see the internal scheduling shell or any admin route.
    if (role === 'super') {
        return <SuperPortalPage />;
    }

    // Default landing depends on role
    const defaultPath = role === 'field' ? '/field' : '/board';

    return (
        <AppShell>
            <Routes>
                <Route path="/" element={<Navigate to={defaultPath} replace />} />
                <Route path="/board" element={<BoardPage />} />
                <Route path="/field" element={<FieldDashboard />} />
                <Route
                    path="/review"
                    element={role === 'admin' ? <ReviewQueuePage /> : <Navigate to={defaultPath} replace />}
                />
                <Route
                    path="/dispatch"
                    element={role === 'admin' ? <DispatchBoardPage /> : <Navigate to={defaultPath} replace />}
                />
                <Route
                    path="/invoicing"
                    element={role === 'admin' ? <InvoiceQueuePage /> : <Navigate to={defaultPath} replace />}
                />
                <Route
                    path="/service"
                    element={role === 'admin' ? <ServiceBoardPage /> : <Navigate to={defaultPath} replace />}
                />
                <Route
                    path="/reports"
                    element={role === 'admin' ? <ReportsPage /> : <Navigate to={defaultPath} replace />}
                />
                <Route
                    path="/weather"
                    element={role === 'admin' ? <WeatherPage /> : <Navigate to={defaultPath} replace />}
                />
                <Route
                    path="/admin"
                    element={role === 'admin' ? <AdminPage /> : <Navigate to={defaultPath} replace />}
                />
                <Route
                    path="/activity"
                    element={role === 'viewer' ? <Navigate to={defaultPath} replace /> : <ActivityLogPage />}
                />
                <Route path="*" element={<Navigate to={defaultPath} replace />} />
            </Routes>
        </AppShell>
    );
}
